import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { api } from "api/api";
import { TerminalListItemDto } from "api/generated/models";
import { AppThunk } from "app/store";
import dayjs from "dayjs";
import { FeatureState } from "models/FeatureState";
import { Pagination } from "models/Pagination";
import { Transaction } from "models/Transaction";
import { downloadFile } from "services/downloadService";

interface State {
  featureState: FeatureState;
  pagination: Pagination;
  exportState: FeatureState;
  transactions?: Transaction[] | null;
  terminals?: TerminalListItemDto[] | null;
}

const initialState: State = {
  featureState: FeatureState.Init,
  exportState: FeatureState.Init,
  terminals: [],
  pagination: {
    pageNumber: 1,
    pageSize: 10,
  },
};

const slice = createSlice({
  name: "transactions",
  initialState,
  reducers: {
    setFeatureState(state, action: PayloadAction<FeatureState>) {
      state.featureState = action.payload;
    },
    setPagination(state, action: PayloadAction<Pagination>) {
      state.pagination = action.payload;
    },
    setExportState(state, action: PayloadAction<FeatureState>) {
      state.exportState = action.payload;
    },
    getTerminalsSuccess(
      state,
      action: PayloadAction<{
        terminals?: TerminalListItemDto[] | null;
      }>
    ) {
      state.terminals = action.payload.terminals;
    },
    getTransactionsSuccess(
      state,
      action: PayloadAction<{
        transactions?: Transaction[] | null;
        pagination: Pagination;
      }>
    ) {
      state.pagination = action.payload.pagination;
      state.transactions = action.payload.transactions;
      state.featureState = FeatureState.Success;
    },
  },
});

const {
  setFeatureState,
  setPagination,
  setExportState,
  getTerminalsSuccess,
  getTransactionsSuccess,
} = slice.actions;

const getTransactions =
  (
    merchantId: number,
    searchFor?: string,
    terminalId?: number,
    from?: Date,
    to?: Date,
    pageNumber?: number,
    pageSize?: number
  ): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setFeatureState(FeatureState.Loading));
    const { pagination } = getState().transactions;
    const size = pageSize || pagination.pageSize;
    const number = pageNumber || pagination.pageNumber;

    try {
      const fromDate = dayjs(from).format("YYYY.MM.DD.");
      const toDate = dayjs(to).format("YYYY.MM.DD.");
      const result = await api.transaction.transactionGetAll(
        merchantId,
        fromDate,
        toDate,
        terminalId,
        searchFor,
        size,
        number
      );
      const { data, ...pagination } = result.data;

      dispatch(
        getTransactionsSuccess({
          transactions: data,
          pagination,
        })
      );
    } catch (error) {
      dispatch(setFeatureState(FeatureState.Error));
    }
  };

const getTerminals =
  (merchantId: number): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setFeatureState(FeatureState.Loading));

    try {
      const result = await api.terminal.terminalGetAllTerminals(merchantId);
      const data = result.data;

      dispatch(
        getTerminalsSuccess({
          terminals: data,
        })
      );
    } catch (error) {
      dispatch(setFeatureState(FeatureState.Error));
    }
  };

const exportTransactions =
  (
    merchantId: number,
    searchFor: string = "",
    from: Date,
    to: Date,
    terminalId?: number
  ): AppThunk =>
  async (dispatch) => {
    dispatch(setExportState(FeatureState.Loading));

    try {
      const fromDate = dayjs(from).format("YYYY-MM-DD");
      const toDate = dayjs(to).format("YYYY-MM-DD");
      const response = await api.transaction.transactionExport(
        merchantId,
        terminalId,
        fromDate,
        toDate,
        searchFor,
        { responseType: "blob" }
      );

      downloadFile(response);
      dispatch(setExportState(FeatureState.Success));
    } catch (error) {
      dispatch(setExportState(FeatureState.Error));
    }
  };

export const transactionsActions = {
  setPagination,
  getTerminals,
  getTransactions,
  exportTransactions,
};

export const transactionsReducer = slice.reducer;
